import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export const AuthProvider = ({ children }) => {
  // Initialize token from localStorage if available
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Update localStorage whenever the token changes
  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }, [token]);

  const login = (newToken) => {
    setToken(newToken);
    // You might want to decode the token here to get user info if needed
  };

  const logout = () => {
    setToken(null);
    // Optionally clear other user-related state
  };

  // Function to refresh the token
  const refreshToken = async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      
      const response = await axios.post(
        `${API_URL}/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data && response.data.token) {
        setToken(response.data.token);
        return response.data.token;
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // If refresh fails, log the user out
      logout();
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  // Create an axios instance with interceptors to handle token expiration
  const authAxios = axios.create({
    baseURL: API_URL
  });

  // Add request interceptor to add the token to all requests
  authAxios.interceptors.request.use(
    (config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add response interceptor to handle token expiration
  authAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // If the error is due to token expiration and we haven't tried to refresh yet
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.expired &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        
        // Try to refresh the token
        const newToken = await refreshToken();
        
        if (newToken) {
          // Update the authorization header with the new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          // Retry the original request
          return authAxios(originalRequest);
        }
      }
      
      return Promise.reject(error);
    }
  );

  // Determine if user is authenticated based on token presence
  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ 
      token, 
      isAuthenticated, 
      login, 
      logout, 
      refreshToken,
      authAxios // Provide the axios instance with interceptors
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
